import { ContactShareKind } from '@curbnturf/objects';

/**
 * Spots contact details and off-site payment in guest/host messages, the way
 * Airbnb watches for bookings being taken off the platform: phone numbers,
 * email addresses (also "name at gmail dot com"), links, and payment apps.
 * CurbNTurf's own links are fine. The admin app highlights these, and the server
 * stores each message's kinds for the admin contact-share list.
 */
export type ContactKind = ContactShareKind;

/** Every kind, in the order contactKinds() reports them. */
export const CONTACT_KINDS: readonly ContactKind[] = ['phone', 'email', 'link', 'payment'];

export interface MessagePart {
  text: string;
  kind?: ContactKind;
}

const PATTERNS: [ContactKind, RegExp][] = [
  ['email', /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi],
  [
    'email',
    /\b[A-Z0-9._%+-]+\s*(?:\(at\)|\[at\]|\sat\s)\s*[A-Z0-9-]+\s*(?:\(dot\)|\[dot\]|\sdot\s)\s*(?:com|net|org|edu|us)\b/gi,
  ],
  ['link', /\b(?:https?:\/\/|www\.)[^\s<>()]+/gi],
  // A bare domain, e.g. hipcamp.com/... or mycampground.net. The ending is lowercase-only:
  // "then.Info" or "Thanks.Co-host" is a sentence missing its space, not a link.
  ['link', /\b[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.(?:com|net|org|us|io|co|info|biz|app|site)\b(?:\/[^\s<>()]*)?/g],
  // 10 digits, with or without separators and a leading +1. Not inside a longer
  // number, an id (pi_3N2085550123) or a coordinate (-116.2085550).
  ['phone', /(?<![\w.-])(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?!\w|\.\d)/g],
  // A 7-digit local number needs its separator: 555-0123, 555.0123.
  ['phone', /(?<![\w.$-])\d{3}[.-]\d{4}(?!\w|[.-]\d)/g],
  ['payment', /\b(?:venmo|cash\s?app|zelle|paypal|apple\s?pay|western union|wire transfer)\b/gi],
];

/** CurbNTurf's own site, judged by the link's host (curbnturf.com/x, not evil.com/?curbnturf.com). */
const ownLink = (link: string) => /(?:^|\.)curbnturf\.com$/i.test(link.replace(/^https?:\/\//i, '').split(/[/?#:]/)[0]);

/** The message split into plain text and flagged pieces, in order. */
export function scanContact(text: string | null | undefined): MessagePart[] {
  const body = text ?? '';
  const hits: { start: number; end: number; kind: ContactKind }[] = [];
  for (const [kind, pattern] of PATTERNS) {
    pattern.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(body))) {
      if (kind === 'link' && ownLink(m[0])) continue;
      const start = m.index;
      const end = start + m[0].length;
      // The first pattern to claim a stretch keeps it (an email isn't also a link).
      if (hits.some((h) => start < h.end && end > h.start)) continue;
      hits.push({ start, end, kind });
    }
  }
  hits.sort((a, b) => a.start - b.start);
  const parts: MessagePart[] = [];
  let at = 0;
  for (const h of hits) {
    if (h.start > at) parts.push({ text: body.slice(at, h.start) });
    parts.push({ text: body.slice(h.start, h.end), kind: h.kind });
    at = h.end;
  }
  if (at < body.length || !parts.length) parts.push({ text: body.slice(at) });
  return parts;
}

/** The kinds of contact detail in already-split parts, once each, in CONTACT_KINDS order. */
export function partsContactKinds(parts: MessagePart[]): ContactKind[] {
  const found = new Set(parts.flatMap((p) => (p.kind ? [p.kind] : [])));
  return CONTACT_KINDS.filter((k) => found.has(k));
}

/** The kinds of contact detail a message shares, once each, in CONTACT_KINDS order. */
export function contactKinds(text: string | null | undefined): ContactKind[] {
  return partsContactKinds(scanContact(text));
}
